<template>
  <div class="p-6 max-w-4xl mx-auto font-sans bg-gray-50 min-h-screen">
    <div class="bg-white p-8 rounded-xl shadow-sm">
      <h1 class="text-3xl font-bold text-center mb-2 text-gray-900">{{ $t('release_notes.page_title') }}</h1>
      <p class="text-center text-gray-500 mb-10">{{ $t('release_notes.subtitle') }}</p>
      
      <div class="relative pl-8 border-l-2 border-gray-200 space-y-12 ml-4">
        <div v-for="(release, i) in releases" :key="i" class="relative">
          <!-- Dot -->
          <div 
            class="absolute -left-[11px] top-1 w-5 h-5 rounded-full bg-white border-4 border-blue-500 transition-colors duration-300"
          ></div>
          
          <div class="mb-6 pl-4">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <h2 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-800 to-gray-600">
                {{ $t('release_notes.version') }} {{ release.version }}
              </h2>
              <span class="text-lg font-bold px-3 py-1 rounded-full bg-blue-100 text-blue-600">
                {{ formatDate(release.date) }}
              </span>
            </div>

            <!-- Features -->
            <div v-if="getItemsByType(release.items, 'feature').length" class="mb-6">
              <h3 class="text-lg font-semibold text-green-600 mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                {{ $t('release_notes.features') }}
              </h3>
              <ul class="space-y-3">
                <li v-for="(item, j) in getItemsByType(release.items, 'feature')" :key="j" 
                    class="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
                  <h4 class="font-semibold text-gray-800">{{ $t(`release_notes.${item.titleKey}`) }}</h4>
                  <p class="text-gray-600 mt-1">{{ $t(`release_notes.${item.descKey}`) }}</p>
                  <NuxtLink v-if="item.link" :to="localePath(item.link)" 
                            class="inline-flex items-center mt-2 text-green-600 hover:text-green-800 text-sm font-medium">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                    {{ item.link }}
                  </NuxtLink>
                </li>
              </ul>
            </div>

            <!-- Improvements -->
            <div v-if="getItemsByType(release.items, 'improvement').length" class="mb-6">
              <h3 class="text-lg font-semibold text-blue-600 mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
                {{ $t('release_notes.improvements') }}
              </h3>
              <ul class="space-y-3">
                <li v-for="(item, j) in getItemsByType(release.items, 'improvement')" :key="j" 
                    class="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <h4 class="font-semibold text-gray-800">{{ $t(`release_notes.${item.titleKey}`) }}</h4>
                  <p class="text-gray-600 mt-1">{{ $t(`release_notes.${item.descKey}`) }}</p>
                  <NuxtLink v-if="item.link" :to="localePath(item.link)" 
                            class="inline-flex items-center mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                    {{ item.link }}
                  </NuxtLink>
                </li>
              </ul>
            </div>

            <!-- Bug Fixes -->
            <div v-if="getItemsByType(release.items, 'bugfix').length">
              <h3 class="text-lg font-semibold text-orange-600 mb-3 flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                {{ $t('release_notes.bug_fixes') }}
              </h3>
              <ul class="space-y-3">
                <li v-for="(item, j) in getItemsByType(release.items, 'bugfix')" :key="j" 
                    class="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-500">
                  <h4 class="font-semibold text-gray-800">{{ $t(`release_notes.${item.titleKey}`) }}</h4>
                  <p class="text-gray-600 mt-1">{{ $t(`release_notes.${item.descKey}`) }}</p>
                </li>
              </ul>
            </div>
          </div>
          
          <div v-if="i < releases.length - 1" class="border-b border-gray-100 my-8 w-full"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import releasesData from '~/data/release-notes.json';

const route = useRoute();
const localePath = useLocalePath();
const ogUrl = computed(() => `https://cld.eu${route.fullPath}`);
const { t, locale } = useI18n();

const releases = ref(releasesData);

const getItemsByType = (items, type) => {
  return items.filter(item => item.type === type);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

useHead({
  title: t('seo.seo_release_notes_title'),
  meta: [
    {
      property: 'og:title',
      content: t('seo.seo_release_notes_title'),
    },
    {
      property: 'og:description',
      content: t('seo.seo_release_notes_description'),
    },
    {
      property: 'og:url',
      content: ogUrl.value,
    },
    {
      name: 'description',
      content: t('seo.seo_release_notes_description'),
    },
    {
      property: "og:type",
      content: "website"
    },
    {
      property: "og:site_name",
      content: "CLD Distribution"
    },
  ],
});
</script>
